import { adminInvoke, adminRpc } from './adminApi';

// Delete storage objects as an admin.
//
// Direct supabase.storage.remove() calls from the admin dashboard could never
// work: the only DELETE policy on uxo-uploads (migration 015) is TO
// authenticated and compares auth.uid() against the path prefix, so even now
// that the dashboard carries an admin's session, the admin is not the owner of
// the path. resources has had no DELETE policy since migration 072. Storage
// answers an RLS miss with {data: [], error: null}, so every one of those calls
// looked like a success.
//
// That last detail is why this helper does not just check `error`. It compares
// what came back against what was asked for -- a delete path that can report
// success without deleting anything will eventually do so again.
export async function adminDeleteStorageObjects(bucket, paths) {
  const wanted = [...new Set((paths || []).filter(Boolean))];
  if (wanted.length === 0) {
    return { ok: true, deleted: [], missing: [], error: null };
  }

  const { data, error } = await adminInvoke('admin-delete-storage-objects', { bucket, paths: wanted });

  if (error) {
    return { ok: false, deleted: [], missing: wanted, error: error.message || 'Storage delete failed.' };
  }
  if (data?.error) {
    return { ok: false, deleted: [], missing: wanted, error: data.error };
  }

  const deleted = data?.deleted || [];
  const missing = data?.missing || [];

  return {
    ok: missing.length === 0,
    deleted,
    missing,
    error: missing.length === 0
      ? null
      : `${missing.length} of ${wanted.length} file(s) were not deleted: ${missing.join(', ')}`,
  };
}

// Collect every storage path owned by a tech profile, then delete those files.
//
// Order matters and is not negotiable: admin_delete_user cascades
// tech_documents away, so the paths have to be read before any row is
// removed or they are unrecoverable. Callers must not delete the profile
// unless this resolves ok.
export async function adminDeleteProfileFiles(profileId, userId = null) {
  if (!profileId && !userId) return { ok: true, deleted: [], missing: [], error: null };

  const { data, error } = await adminRpc('admin_collect_profile_files', {
    target_profile_id: profileId || null,
    target_user_id: userId || null,
  });

  if (error) {
    return { ok: false, deleted: [], missing: [], error: `Could not read the profile's files: ${error.message}` };
  }

  const paths = (data || []).map((r) => r.path).filter(Boolean);
  if (paths.length === 0) {
    return { ok: true, deleted: [], missing: [], error: null };
  }

  return adminDeleteStorageObjects('uxo-uploads', paths);
}
